import { ceremonyInputDescriptionSchema, type DocumentInputs } from '@witness/shared/document-inputs';
import type { CeremonyProgressStep } from '@witness/shared/ceremony-progress';
/**
 * Typed HTTP client for the SaaS API surface the console calls.
 *
 * Scope: only the admin/platform endpoints the console needs.
 * Discriminated `{ ok: true, ... } | { ok: false, reason }` results
 * for expected failure modes (duplicate id, unknown tenant); genuine
 * network / 5xx errors throw.
 */
import { WITNESS_API_BASE } from './console-env';
import { platformAuthorizationHeader } from './platform-auth';
import { tenantAuthorizationHeader } from './tenant-auth';

export interface Tenant {
  readonly id: string;
  readonly displayName: string;
  readonly createdAtMs: number;
  readonly updatedAtMs: number;
}

export class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiNotConfiguredError extends Error {
  constructor() {
    super(
      'WITNESS_API_BASE is not set — the current hostname does not ' +
        'resolve to a known API under witnessengineering.com. See ADR 0017.',
    );
    this.name = 'ApiNotConfiguredError';
  }
}

export interface ClioOnboardingAttempt {
  readonly id: string;
  readonly witnessTenantId: string;
  readonly status: 'awaiting_oauth' | 'oauth_complete' | 'configuring' | 'complete' | 'failed';
  readonly clioUserId: string | null;
  readonly clioTenantId: string | null;
  readonly failure: string | null;
  readonly source: 'witness' | 'clio';
}

export interface ActiveWitnessTenant {
  readonly id: string;
  readonly displayName: string;
}

export interface ClioIntegration {
  readonly id: string;
  readonly clioTenantId: string;
  readonly witnessTenantId: string;
  readonly integrationVersion: number;
  readonly clioApiVersion: string;
  readonly status: 'configuring' | 'ready' | 'failed';
  readonly clioFieldSetId: string | null;
  readonly attributes: readonly { attribute: string; clioAttributeId: string }[];
}

/** Active Witness tenant memberships available to the signed-in tenant user. */
export async function listActiveWitnessTenants(): Promise<readonly ActiveWitnessTenant[]> {
  const res = await fetch(`${apiBase()}/tenant/memberships`, {
    headers: await tenantAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`listActiveTenantMemberships failed: ${res.status}`);
  const body = await res.json() as { memberships: Array<{
    tenantId: string;
    tenantDisplayName: string;
  }> };
  return body.memberships.map((membership) => ({
    id: membership.tenantId,
    displayName: membership.tenantDisplayName,
  }));
}

export async function createClioOnboardingAttempt(tenantId: string, source: 'witness' | 'clio' = 'witness') {
  const res = await fetch(`${apiBase()}/integrations/clio/onboarding-attempts`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify({ tenantId, source }),
  });
  if (!res.ok) throw new ApiError(`createClioOnboardingAttempt failed: ${res.status}`);
  return await res.json() as { attemptId: string; authorizationUrl: string };
}

export async function completeClioOAuth(input: { tenantId: string; code: string; state: string }) {
  const res = await fetch(`${apiBase()}/integrations/clio/oauth/callback`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(`completeClioOAuth failed: ${res.status}`);
  return (await res.json() as { attempt: ClioOnboardingAttempt }).attempt;
}

export async function getClioOnboardingAttempt(tenantId: string, attemptId: string) {
  const params = new URLSearchParams({ tenantId });
  const res = await fetch(`${apiBase()}/integrations/clio/onboarding-attempts/${encodeURIComponent(attemptId)}?${params}`, {
    headers: await tenantAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`getClioOnboardingAttempt failed: ${res.status}`);
  return (await res.json() as { attempt: ClioOnboardingAttempt }).attempt;
}

export async function completeClioOnboarding(tenantId: string, attemptId: string) {
  const res = await fetch(`${apiBase()}/integrations/clio/onboarding-attempts/${encodeURIComponent(attemptId)}/complete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify({ tenantId }),
  });
  if (!res.ok) throw new ApiError(`completeClioOnboarding failed: ${res.status}`);
  return (await res.json() as { integration: ClioIntegration }).integration;
}

export async function getClioIntegration(tenantId: string) {
  const params = new URLSearchParams({ tenantId });
  const res = await fetch(`${apiBase()}/integrations/clio?${params}`, { headers: await tenantAuthorizationHeader() });
  if (!res.ok) throw new ApiError(`getClioIntegration failed: ${res.status}`);
  return (await res.json() as { integration: ClioIntegration | null }).integration;
}

function apiBase(): string {
  if (!WITNESS_API_BASE) throw new ApiNotConfiguredError();
  return WITNESS_API_BASE;
}

export interface ListTenantsInput {
  readonly offset?: number;
  readonly limit?: number;
}

export async function listTenants(
  input: ListTenantsInput = {},
  options: { signal?: AbortSignal } = {},
): Promise<readonly Tenant[]> {
  const params = new URLSearchParams();
  if (input.offset !== undefined) params.set('offset', String(input.offset));
  if (input.limit !== undefined) params.set('limit', String(input.limit));
  const query = params.toString();
  const url = `${apiBase()}/tenants${query ? `?${query}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    signal: options.signal,
    headers: await platformAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`listTenants failed: ${res.status}`);
  const body = (await res.json()) as { tenants: Tenant[] };
  return body.tenants;
}

export type CreateTenantResult =
  | { readonly ok: true; readonly tenant: Tenant }
  | { readonly ok: false; readonly reason: 'duplicate_id' | 'invalid' };

export async function createTenant(input: {
  id: string;
  displayName: string;
}): Promise<CreateTenantResult> {
  const res = await fetch(`${apiBase()}/tenants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await platformAuthorizationHeader()),
    },
    body: JSON.stringify(input),
  });
  if (res.status === 201) {
    const body = (await res.json()) as { tenant: Tenant };
    return { ok: true, tenant: body.tenant };
  }
  if (res.status === 409) return { ok: false, reason: 'duplicate_id' };
  if (res.status === 400) return { ok: false, reason: 'invalid' };
  throw new ApiError(`createTenant failed: ${res.status}`);
}

export async function getPlatformTenant(id: string): Promise<Tenant | null> {
  const res = await fetch(`${apiBase()}/platform/tenants/${encodeURIComponent(id)}`, {
    headers: await platformAuthorizationHeader(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(`getPlatformTenant failed: ${res.status}`);
  return ((await res.json()) as { tenant: Tenant }).tenant;
}

export type TenantRole = 'ADMIN' | 'AGENT';
export interface TenantUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: TenantRole;
  readonly grantedAtMs: number;
  readonly disabledAtMs: number | null;
}

export interface TenantPoolUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly createdAtMs: number;
  readonly disabledAtMs: number | null;
}

export async function listPlatformUsers(): Promise<readonly TenantPoolUser[]> {
  const res = await fetch(`${apiBase()}/platform/users`, {
    headers: await platformAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`listPlatformUsers failed: ${res.status}`);
  return ((await res.json()) as { users: TenantPoolUser[] }).users;
}

export async function disablePlatformUser(email: string): Promise<void> {
  const res = await fetch(`${apiBase()}/platform/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await platformAuthorizationHeader()) },
    body: JSON.stringify({ status: 'disabled' }),
  });
  if (!res.ok) throw new ApiError(`disablePlatformUser failed: ${res.status}`);
}

export async function listPlatformMemberships(tenantId: string): Promise<readonly TenantUser[]> {
  const res = await fetch(`${apiBase()}/platform/tenants/${encodeURIComponent(tenantId)}/memberships`, {
    headers: await platformAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`listPlatformMemberships failed: ${res.status}`);
  return ((await res.json()) as { memberships: TenantUser[] }).memberships;
}

export async function grantPlatformMembership(
  tenantId: string,
  input: { email: string; role: TenantRole },
): Promise<TenantUser> {
  const res = await fetch(`${apiBase()}/platform/tenants/${encodeURIComponent(tenantId)}/memberships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await platformAuthorizationHeader()) },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(`grantPlatformMembership failed: ${res.status}`);
  return ((await res.json()) as { membership: TenantUser }).membership;
}

export async function disablePlatformTenantUser(tenantId: string, userId: string): Promise<void> {
  const res = await fetch(`${apiBase()}/platform/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await platformAuthorizationHeader()) },
    body: JSON.stringify({ status: 'disabled' }),
  });
  if (!res.ok) throw new ApiError(`disablePlatformTenantUser failed: ${res.status}`);
}

export async function listTenantUsers(tenantId: string): Promise<readonly TenantUser[]> {
  const res = await fetch(`${apiBase()}/tenants/${encodeURIComponent(tenantId)}/users`, {
    headers: await tenantAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError(`listTenantUsers failed: ${res.status}`);
  return ((await res.json()) as { users: TenantUser[] }).users;
}

export async function inviteTenantUser(
  tenantId: string,
  input: { email: string; role: TenantRole },
): Promise<TenantUser> {
  const res = await fetch(`${apiBase()}/tenants/${encodeURIComponent(tenantId)}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(`inviteTenantUser failed: ${res.status}`);
  return ((await res.json()) as { user: TenantUser }).user;
}

export async function disableTenantUser(tenantId: string, userId: string): Promise<void> {
  const res = await fetch(`${apiBase()}/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify({ status: 'disabled' }),
  });
  if (!res.ok) throw new ApiError(`disableTenantUser failed: ${res.status}`);
}

export async function getTenant(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<Tenant | null> {
  const res = await fetch(`${apiBase()}/tenants/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal: options.signal,
    headers: await tenantAuthorizationHeader(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(`getTenant failed: ${res.status}`);
  const body = (await res.json()) as { tenant: Tenant };
  return body.tenant;
}

// --------------------------------------------------------------
// Ceremonies (tenant-scoped, ADR 0016)
// --------------------------------------------------------------

export type CeremonyState = 'in_flight' | 'returned' | 'completed' | 'failed';

export interface CeremonySummary {
  readonly id: string;
  readonly templateId: string;
  readonly state: CeremonyState;
  readonly createdAtMs: number;
  readonly expiresAtMs: number;
}

export interface CeremonyEvent {
  readonly id: string;
  readonly ceremonyId: string;
  readonly type: string;
  readonly occurredAt: number;
  readonly payload?: Record<string, unknown>;
}

export interface Ceremony {
  readonly documentInputs?: readonly { label: string; value: string }[];
  /** Absent on older servers; null when the workflow cannot describe progress. */
  readonly progress?: readonly CeremonyProgressStep[] | null;
  readonly id: string;
  readonly templateId: string;
  readonly state: CeremonyState;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly events: readonly CeremonyEvent[];
}

export interface ListCeremoniesInput {
  readonly offset?: number;
  readonly limit?: number;
}

export async function listCeremonies(
  tenantId: string,
  input: ListCeremoniesInput = {},
  options: { signal?: AbortSignal } = {},
): Promise<readonly CeremonySummary[]> {
  const params = new URLSearchParams();
  if (input.offset !== undefined) params.set('offset', String(input.offset));
  if (input.limit !== undefined) params.set('limit', String(input.limit));
  const query = params.toString();
  const url = `${apiBase()}/tenants/${encodeURIComponent(tenantId)}/ceremonies${
    query ? `?${query}` : ''
  }`;
  const res = await fetch(url, { method: 'GET', signal: options.signal, headers: await tenantAuthorizationHeader() });
  if (!res.ok) throw new ApiError(`listCeremonies failed: ${res.status}`);
  const body = (await res.json()) as { ceremonies: CeremonySummary[] };
  return body.ceremonies;
}

export async function getCeremony(
  tenantId: string,
  ceremonyId: string,
  options: { signal?: AbortSignal } = {},
): Promise<Ceremony | null> {
  const url = `${apiBase()}/tenants/${encodeURIComponent(
    tenantId,
  )}/ceremonies/${encodeURIComponent(ceremonyId)}`;
  const res = await fetch(url, { method: 'GET', signal: options.signal, headers: await tenantAuthorizationHeader() });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(`getCeremony failed: ${res.status}`);
  const body = (await res.json()) as { ceremony: Ceremony };
  return body.ceremony;
}

export async function getCeremonyInputDescription(tenantId: string, templateId: string, signal?: AbortSignal) {
  const res = await fetch(`${apiBase()}/tenants/${encodeURIComponent(tenantId)}/ceremony-templates/${encodeURIComponent(templateId)}/inputs`, {
    signal, headers: await tenantAuthorizationHeader(),
  });
  if (!res.ok) throw new ApiError('Unable to load the template inputs. Please retry.');
  return ceremonyInputDescriptionSchema.parse(await res.json());
}

export interface CreateCeremonyInput {
  readonly documentInputs?: DocumentInputs;
  readonly templateId: string;
  readonly subjectName: string;
  readonly deliveryEmail: string;
}

export interface CreateCeremonySuccess {
  readonly ceremonyId: string;
  readonly templateId: string;
  readonly state: CeremonyState;
  readonly createdAt: number;
  readonly deliveredTo: string;
}

export type CreateCeremonyResult =
  | { readonly ok: true; readonly ceremony: CreateCeremonySuccess }
  | {
      readonly ok: false;
      readonly reason:
        | 'unknown_template'
        | 'no_eligible_card'
        | 'invalid_input'
        | 'server_error';
    };

export async function createCeremony(
  tenantId: string,
  input: CreateCeremonyInput,
): Promise<CreateCeremonyResult> {
  const url = `${apiBase()}/tenants/${encodeURIComponent(
    tenantId,
  )}/ceremonies`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await tenantAuthorizationHeader()) },
    body: JSON.stringify({
      templateId: input.templateId,
      subject: { name: input.subjectName },
      documentInputs: input.documentInputs,
      deliveryEmail: input.deliveryEmail,
    }),
  });
  if (res.status === 201) {
    return { ok: true, ceremony: (await res.json()) as CreateCeremonySuccess };
  }
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (body.error === 'unknown_template') {
      return { ok: false, reason: 'unknown_template' };
    }
    return { ok: false, reason: 'invalid_input' };
  }
  if (res.status === 503) return { ok: false, reason: 'no_eligible_card' };
  return { ok: false, reason: 'server_error' };
}
